import { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, LinearProgress, Snackbar, Typography,
} from '@mui/material';
import { Clear, ListAlt, PictureAsPdf } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  downloadBulkTestSheets, fetchReportPdf,
  saveBlobToDisk, saveMergedReportsByType,
} from '../../services/reportService';

/** Context per selected row, keyed by WorkOrderDetailKey — used to group the
 *  reports by test type and name each merged PDF. */
export interface BulkReportRowInfo {
  testName?: string | null;
  workOrderId?: string | null;
  projectId?: string | null;
}

interface TestsBulkBarProps {
  selectedKeys: string[];
  rowInfoByKey?: Record<string, BulkReportRowInfo>;
  onClear: () => void;
}

/**
 * Bulk actions for the selected tests: merged data-entry sheets (server-side)
 * and merged report PDFs — ONE file per test type, reports in selection
 * order within each (lab request: no ZIPs, no single giant mixed file).
 * Report fetches ride the report archive — unchanged tests are served from
 * blob storage, so re-downloading a batch is fast after the first run.
 */
export const TestsBulkBar = ({ selectedKeys, rowInfoByKey, onClear }: TestsBulkBarProps) => {
  const theme = useTheme();
  const [sheetsBusy, setSheetsBusy] = useState(false);
  const [zip, setZip] = useState<{ current: number; total: number } | null>(null);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  if (selectedKeys.length === 0) return null;

  const handleSheets = async () => {
    setSheetsBusy(true);
    try {
      const result = await downloadBulkTestSheets(selectedKeys);
      const skippedNote = result.skipped.length > 0 ? ` (${result.skipped.length} skipped)` : '';
      setSnack({ message: `Test sheets ready: ${result.generated} rendered${skippedNote}.`, severity: 'success' });
    } catch (err) {
      setSnack({ message: err instanceof Error ? err.message : 'Failed to generate test sheets.', severity: 'error' });
    } finally {
      setSheetsBusy(false);
    }
  };

  const handleMergedReports = async () => {
    setZip({ current: 0, total: selectedKeys.length });
    const fetched: { key: string; blob: Blob; filename: string }[] = [];
    const failures: number[] = [];
    for (let i = 0; i < selectedKeys.length; i += 1) {
      setZip({ current: i + 1, total: selectedKeys.length });
      try {
        const { blob, filename } = await fetchReportPdf(selectedKeys[i]);
        fetched.push({ key: selectedKeys[i], blob, filename });
      } catch {
        failures.push(i);
      }
    }
    try {
      let files = 0;
      if (fetched.length === 1) {
        // Single report keeps the server's own per-test filename.
        saveBlobToDisk(fetched[0].blob, fetched[0].filename);
        files = 1;
      } else if (fetched.length > 1) {
        // Cross-project surface: scope falls back to the project when the
        // selection sits in one project, else a generic 'Tests'.
        const projects = [...new Set(fetched
          .map((f) => rowInfoByKey?.[f.key]?.projectId)
          .filter((p): p is string => !!p))];
        files = await saveMergedReportsByType(
          fetched.map((f) => ({
            blob: f.blob,
            testName: rowInfoByKey?.[f.key]?.testName ?? 'Report',
            workOrderId: rowInfoByKey?.[f.key]?.workOrderId,
          })),
          projects.length === 1 ? projects[0] : 'Tests',
        );
      }
      setSnack(failures.length > 0
        ? { message: `${files} PDF file${files === 1 ? '' : 's'} downloaded (${fetched.length} reports), ${failures.length} failed.`, severity: 'error' }
        : { message: `${files} PDF file${files === 1 ? '' : 's'} downloaded — ${fetched.length} report${fetched.length === 1 ? '' : 's'}, one file per test type.`, severity: 'success' });
    } catch (err) {
      setSnack({ message: err instanceof Error ? err.message : 'Failed to merge report PDFs.', severity: 'error' });
    }
    setZip(null);
  };

  const busy = sheetsBusy || zip !== null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 0.75,
        mb: 1.25,
        borderRadius: '10px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {selectedKeys.length} selected
      </Typography>
      <Button
        size="small"
        startIcon={sheetsBusy ? <CircularProgress size={14} /> : <ListAlt />}
        onClick={handleSheets}
        disabled={busy}
        sx={{ textTransform: 'none' }}
      >
        Test sheets (merged PDF)
      </Button>
      <Button
        size="small"
        startIcon={zip ? <CircularProgress size={14} /> : <PictureAsPdf />}
        onClick={handleMergedReports}
        disabled={busy}
        sx={{ textTransform: 'none' }}
      >
        {zip ? `Reports ${zip.current}/${zip.total}…` : 'Reports (merged PDF)'}
      </Button>
      <Button
        size="small"
        startIcon={<Clear />}
        onClick={onClear}
        disabled={busy}
        sx={{ textTransform: 'none', color: 'text.secondary', ml: 'auto' }}
      >
        Clear selection
      </Button>
      {zip && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress variant="determinate" value={(zip.current / zip.total) * 100} sx={{ borderRadius: 1 }} />
        </Box>
      )}
      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};
